import i18n from 'services/i18n'
import { formattedDateForCountry } from 'components/shared/mixins/dateHelper'
import { convertMinutesToHours } from 'components/shared/mixins/convertMinutesToHours'
import config from 'services/config'
import jsPDF from 'jspdf'

import { PPAgrandirMedium } from 'assets/fonts/certificate/PPAgrandid-Medium.js'
import { PlusJakartaSansRegular } from 'assets/fonts/certificate/PlusJakartaSans-Regular.js'
import { PlusJakartaSansMedium } from 'assets/fonts/certificate/PlusJakartaSans-Medium.js'
import { PlusJakartaSansBold } from 'assets/fonts/certificate/PlusJakartaSans-Bold.js'
import { fontIcomoonNormal } from 'assets/fonts/icomoon-normal.js'

const singleBackGroundImage = require('@/assets/images/certificates/fi/SingleCertificateBackground.js')
const listBackGroundImage = require('@/assets/images/certificates/fi/ListCertificateBackground.js')

const singleBackGroundImageDk = require('@/assets/images/certificates/dk/SingleCertificateBackground.js')
const listBackGroundImageDk = require('@/assets/images/certificates/dk/ListCertificateBackground.js')

function createSingleCertificate (course, userName) {
    let doc = getNewPdfObject();

    if (course.entityType === 'learningPath') {
        // Update properties from learningPath to course properties
        course.courseTitle = course.learningPathName; // From learningPathName -> courseTitle
        course.courseDescription = course.learningPathDescription; // From LearningPathDescription -> courseDescription
        course.coursePublishDate = course.learningPathPublishDate; // From learningPathPublishDate -> coursePublishDate

        // Handle instructors (support for array of instructor objects)
        if (Array.isArray(course.instructors)) {
            course.instructor = course.instructors.map(instructor => instructor.name).join(', ');
        }

        // Handle the authorizations (from course.courseCertificates and durations)
        course.authorizations = course.courseCertificates
            .map(cert => {
                return cert.durations.map(duration => {
                    return {
                        category: duration.category,
                        credits: duration.credits
                    };
                });
            })
            .flat(); // Flatten the nested array to one level

        // Optionally, handle the duration if needed, for example, sum the durations
        course.totalDuration = course.courseCertificates
        .map(certificate => certificate.duration || 0) // Extract durations, default to 0 if missing
        .reduce((total, duration) => total + duration, 0); // Sum them up
    }

    // Define page and border size as before
    const pageHeight = 210;
    const pageWidth = 297;
    const borderSize = 20;  // Border thickness
    const borderRadius = 5;
    const bottomMargin = 30;  // Space from the bottom edge

    // Set background color and inner white background with rounded corners
    doc.setFillColor(227, 242, 255); // #E3F2FF
    doc.rect(0, 0, pageWidth, pageHeight, "F"); // Full-page background
    doc.setFillColor(255, 255, 255); // White background
    doc.roundedRect(
        borderSize / 2,  // X Position
        borderSize / 2,  // Y Position
        pageWidth - borderSize,  // Width
        pageHeight - borderSize, // Height
        borderRadius,  // Horizontal corner radius
        borderRadius,  // Vertical corner radius
        "F" // Fill only
    );

    if(config.appLanguage === 'dk') {
        doc.addImage(singleBackGroundImageDk.default.backgroundImage, 'PNG', 0, 0, 297, 210);
    } else {
        doc.addImage(singleBackGroundImage.default.backgroundImage, 'PNG', 0, 0, 297, 210);
    }

    const contentStartX = borderSize / 2;
    const contentWidth = pageWidth - borderSize; // Width of the white background

    // Title: Certificate
    doc.setFont("PPAgrandir-Medium", "normal");
    doc.setFontSize(45);
    doc.setTextColor(1, 16, 67); // RGB values
    doc.text(i18n.t('Certificates.certificate_title'), pageWidth / 2, 35, { align: "center" });

    // Certificate Holder's Name (Centered)
    doc.setFontSize(25);
    const holderName = userName; // Replace dynamically
    doc.setTextColor(71, 102, 255); // #4766FF
    const nameY = 60; // Position for the name text
    doc.text(holderName, pageWidth / 2, nameY, { align: "center" });

    // Border bottom for certificate holder
    const borderMargin = 15;  // Padding from the white background
    const borderY = nameY + 3;  // Position slightly below the name
    const borderHeight = 0.1;  // Border thickness

    doc.setDrawColor(217, 217, 217); // #D9D9D9
    doc.setLineWidth(borderHeight);
    doc.line(contentStartX + borderMargin, borderY, contentStartX + contentWidth - borderMargin, borderY);

    // Add "has completed the course" text (Centered)
    const completedTextY = borderY + 8;
    doc.setFont("PlusJakartaSans-Regular", "normal");
    doc.setFontSize(12);
    doc.setTextColor('#212120');
    doc.text(i18n.t('Certificates.has_completed_course'), pageWidth / 2, completedTextY, { align: "center" });

    // Add Course Title (40px Below)
    const courseTitleY = completedTextY + 20;
    doc.setTextColor(1, 16, 67); // RGB values
    const courseTitle = course.courseTitle; // Dynamic title
    doc.setFont("PPAgrandir-Medium", "normal");
    doc.setFontSize(22);

    // Wrap text if too long
    const courseTitleLines = doc.splitTextToSize(courseTitle, 240);
    const courseTitleHeight = courseTitleLines.length * 10; // Adjust line height dynamically

    doc.text(courseTitleLines, pageWidth / 2, courseTitleY, { align: "center" });

    // Adjust description position dynamically
    const descriptionY = courseTitleY + courseTitleHeight + 5;

    const plainTextCourseDescription = new DOMParser().parseFromString(course.courseDescription, "text/html").body.textContent;

    const maxChars = 600;
    // Function to truncate text
    const truncateText = (text, maxLength) => {
        return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    };

    const description = plainTextCourseDescription;
    const descriptionMaxWidth = contentWidth - (borderMargin * 2); // Keep inside white background
    const truncatedDescription = truncateText(description, maxChars);

    doc.setFont("PlusJakartaSans-Regular", "normal");
    doc.setFontSize(12);
    doc.setTextColor('#444444');
    doc.text(truncatedDescription, contentStartX + borderMargin, descriptionY, {
        maxWidth: descriptionMaxWidth,
        align: "justify"
    });

    const getLevelFormat = (course) => {
        if (course.entityType === 'learningPath' && Array.isArray(course.levels)) {
            return course.levels
                .map(level => i18n.t(`Certificates.level_${level}`))
                .join(', ');
        }

        return i18n.t(`Certificates.level_${course.level}`);
    };

    // Define 3-column details section
    let details = [
        { label: i18n.t('Certificates.course_format'), value: courseFormat(course)},
        { label: i18n.t('Certificates.level'), value: getLevelFormat(course) },
        { label: i18n.t('Certificates.duration'), value: convertMinutesToHours(course.duration)},
        { label: i18n.t('Certificates.instructor'), value: course.instructor },
        { label: i18n.t('Certificates.published_date'), value: formattedDateForCountry(course.coursePublishDate) },
        { label: i18n.t('Certificates.certificate_id'), value: course.id }
    ];
    if (config.appLanguage === 'dk') {
        details = details.filter(detail => detail.label !== i18n.t('Certificates.published_date'));
    }
    // Position the details section near the bottom
    const detailsStartY = pageHeight - bottomMargin; // Adjust to align with bottom margin
    const columnWidth = (contentWidth - (2 * borderMargin)) / 3;
    const leftColumnX = contentStartX + borderMargin;
    const rightColumnX = contentStartX + contentWidth - columnWidth; // Align the right column to the end

    // Split details into Left & Right Columns
    const leftDetails = details.slice(0, 3);
    const rightDetails = details.slice(3, 6);

    let yOffset = -10;

    doc.setFontSize(10);

    // **Left Column** (Label and Value on Same Row)
    leftDetails.forEach((item) => {
        let yPos = detailsStartY + yOffset;
        doc.setFont("PlusJakartaSans-Bold", "normal");

        // Calculate the width of the label
        const labelWidth = doc.getTextWidth(item.label);  // Includes the colon
        const margin = 1; // Fixed margin between label and value

        // Move the value based on label width + margin
        doc.text(`${item.label}`, leftColumnX, yPos);
        doc.setFont("PlusJakartaSans-Regular", "normal");
        const valueLines = doc.splitTextToSize(item.value, 80); // Wrap text if too long
        doc.text(valueLines, leftColumnX + labelWidth + margin, yPos);

        yOffset += 6; // Space between each row
    });

    // **Center Column (Completed Date)**
    const date = new Date(course.certificationDate ?? course.date);
    const formattedDate = formattedDateForCountry(date);
    doc.setFont("PPAgrandir-Medium", "normal");
    doc.setFontSize(9);
    doc.setFont("PlusJakartaSans-Bold", "normal");
    doc.text(formattedDate.toString(), pageWidth / 2, detailsStartY - 7 , { align: "center" });

    // **Right Column** (Label and Value on Same Row)
    doc.setFontSize(10);
    yOffset = -10;
    rightDetails.forEach((item) => {
        let yPos = detailsStartY + yOffset;
        doc.setFont("PlusJakartaSans-Bold", "normal");
        // Calculate the width of the label
        const labelWidth = doc.getTextWidth(item.label);
        const margin = 1; // Fixed margin between label and value

        // Wrap text (Instructor wraps at 35 characters per line)
        const valueLines = doc.splitTextToSize(item.value, 55);

        // Draw label
        doc.text(`${item.label}`, rightColumnX, yPos);
        doc.setFont("PlusJakartaSans-Regular", "normal");

        // Draw wrapped value text
        doc.text(valueLines, rightColumnX + labelWidth + margin, yPos);

        // Adjust spacing
        if (item.label === i18n.t('Certificates.instructor')) {
            yOffset += 6 * valueLines.length; // Extra space if instructor spans multiple lines
        } else {
            yOffset += 6; // Normal spacing for other labels
        }
    });

    const getAuthorizations = (course) => {
        let authorizations = [];
        if (course.entityType === "learningPath") {
            authorizations = course.authorizations.map(auth => `${auth.category}: ${auth.credits}`);
        } else {
            authorizations = course.durations.map(duration => `${duration.category}: ${duration.credits}`);
        }

        // If authorizationId exists, add it to the list
        if (course.authorizationId) {
            authorizations.push(`ID: ${course.authorizationId}`);
        }

        return authorizations;
    };

    const authorizations = getAuthorizations(course);

    // Authorization Section
    const authorizationY = 150; // Below description
    const authorizationBoxX = contentStartX + borderMargin;
    const authorizationBoxWidth = descriptionMaxWidth;
    const authBackgroundColor = [227, 242, 255]; // #E3F2FF

    // Styling settings
    const textPaddingX = 4;
    const textPaddingY = 3.8;
    const itemSpacing = 2;
    const rowSpacing = 3;
    const cornerRadius = 3.8;
    const maxRowWidth = authorizationBoxWidth - 10;

    let currentX = authorizationBoxX;
    let currentY = authorizationY + 10;
    let rowHeight = 7;
    let totalHeight = rowHeight;

    // Measure text widths dynamically
    doc.setFontSize(10);
    doc.setTextColor('#212120');

    if (authorizations.length > 0) {
        const labelText = i18n.t('CertificateList.authorization_hours');
        const labelWidth = doc.getTextWidth(labelText);

        // Draw the label
        doc.setFont("PlusJakartaSans-Regular", "normal");
        doc.setFontSize(11);
        doc.text(labelText, currentX, currentY - 20);

        // Offset currentX to start placing tags right after the label
        currentX += labelWidth + itemSpacing + 6;
    }

    authorizations.forEach((auth) => {
        // Find the last colon to separate category from credits
        const lastColonIndex = auth.lastIndexOf(':');
        if (lastColonIndex === -1) return; // Skip if format is unexpected

        const category = auth.substring(0, lastColonIndex + 1).trim(); // includes colon
        const credits = auth.substring(lastColonIndex + 1).trim(); // just the number or text

        // Measure category (bold)
        doc.setFont("PlusJakartaSans-Bold", "normal");
        doc.setFontSize(9);
        const categoryWidth = doc.getTextWidth(category);

        // Measure credits (regular)
        doc.setFont("PlusJakartaSans-Regular", "normal");
        const creditsWidth = doc.getTextWidth(` ${credits}`);

        const textWidth = categoryWidth + creditsWidth + textPaddingX * 2;

        // Wrap to next line if needed
        if (currentX + textWidth + itemSpacing > authorizationBoxX + maxRowWidth) {
            currentX = authorizationBoxX + 5;
            currentY += rowHeight + rowSpacing;
            totalHeight += rowHeight + rowSpacing;
        }

        // Draw background
        doc.setDrawColor('#CFE6FE');
        doc.setLineWidth(0.3);
        doc.setFillColor(...authBackgroundColor);
        doc.roundedRect(
            currentX, currentY - textPaddingY - 21,
            textWidth, rowHeight,
            cornerRadius, cornerRadius, "F"
        );

        // Draw category (bold)
        doc.setFont("PlusJakartaSans-Bold", "normal");
        doc.setFontSize(9);
        doc.text(category, currentX + textPaddingX, currentY - 20.5);

        // Draw credits (regular)
        doc.setFont("PlusJakartaSans-Regular", "normal");
        doc.text(` ${credits}`, currentX + textPaddingX + categoryWidth, currentY - 20.5);

        // Move X for next tag
        currentX += textWidth + itemSpacing;
    });


    let footerText = getFooterCompanyName()
    const footerTextY = pageHeight - (borderSize / 4.5); // Adjust to position in blue border

    doc.setFont("PlusJakartaSans-Regular", "normal");
    doc.setFontSize(12);
    doc.setTextColor(71, 102, 255);
    doc.text(footerText, pageWidth / 2, footerTextY, { align: "center" });

    if (course.entityType === 'learningPath') {
        createListForLearningPathCourses(doc, course.courseCertificates, course.courseTitle)
    }

    doc.setProperties({
        title: i18n.t('Certificates.training_certificate')
    })

    window.open(doc.output('bloburl'))
}

function createListForLearningPathCourses (doc, coursesForDocument, learningPathTitle) {
        // Define page size, margins, and properties for landscape orientation
        const pageWidth = 297;  // A4 width in landscape mode
        const pageHeight = 210; // A4 height in landscape mode
        const bottomMargin = 5; // Space from bottom edge
        const lineHeight = 7;    // Space between lines
        const infoLineHeight = 5; // Smaller spacing for course info section
        const borderSize = 15;   // Border thickness for the content area
        const borderRadius = 5;  // Corner radius for the white content area

        // Define column positions
        const colStudy = 10;
        const colAuthorization = 140; // Moved left for more space
        const colDate = 240;

        let y = 20;  // Initial Y position
        let pageNumber = 1;  // Start with Page 1
        let courseNumber = 1; // Numbering for standalone courses

        // Function to check if a new page is needed BEFORE adding a course
        const ensureCourseFits = (titleLines, authLines, infoLines, listIsLearningPath) => {
            const requiredSpace =
                titleLines.length * lineHeight +
                authLines.length * lineHeight +
                infoLines.length * infoLineHeight + 5;

            if (y + requiredSpace >= pageHeight - bottomMargin - 5) {
                startCoursesSection(listIsLearningPath, true); // Move to a new page before adding course
            }
        };

        // Add page background color and rounded top border
        const addPageBackground = () => {
            doc.setFillColor(227, 242, 255); // #E3F2FF light blue color
            doc.rect(0, 0, pageWidth, pageHeight, 'F'); // Fill entire page with the background color

            doc.setFillColor(255, 255, 255); // White content area
            const startY = pageNumber === 2 ? y + 25 : y;
            doc.roundedRect(
                borderSize / 2,
                startY,
                pageWidth - borderSize,
                pageHeight - borderSize - startY,
                borderRadius,
                borderRadius,
                'F'
            );
        };

        // Ensure Courses section starts on a new page correctly
        const startCoursesSection = (listIsLearningPath, secondCoursePage) => {
            doc.addPage();
            pageNumber++;
            pageNumber === 2 ? y = 10 : y = 20;
            addPageBackground();

            addColumnHeaders(); // First, add the headers so the title isn't above them.
            doc.setTextColor('#212120');
            // Now, add the "Courses" section title
            doc.setFont("PPAgrandir-Medium", "normal");
            if (!listIsLearningPath && !secondCoursePage) {
                doc.setFontSize(14);
                doc.text(i18n.t('Certificates.courses'), 10, y);
            }
            doc.setFontSize(10);
            doc.text(`${i18n.t('CertificateList.page')} ${pageNumber}`, pageWidth - 20, 7);

            doc.setFontSize(10);
            doc.setFont("PlusJakartaSans-Regular", "normal");
            y += lineHeight * 1.5;
        };

        // Function to add column headers
        const addColumnHeaders = () => {
            doc.setFontSize(10);
            doc.setFillColor(71, 102, 255); // Blue color for header
            doc.setDrawColor(71, 102, 255); // Border color (optional)

            let x = colStudy - 2.7;
            let yTop = y - 7;
            let width = colDate - colStudy + 52.4;
            let height = lineHeight * 1.5;
            let radius = 2; // Radius for top corners

            // Begin custom drawing
            doc.setFillColor(71, 102, 255);
            doc.setDrawColor(71, 102, 255);

            // Create a path manually using lines and curves
            doc.lines(
                [
                    [width - 2 * radius, 0], // Top horizontal line
                    [radius, radius], // Top-right curve
                    [0, height - radius], // Right vertical line
                    [-width, 0], // Bottom horizontal line
                    [0, -(height - radius)], // Left vertical line
                    [radius, -radius], // Top-left curve
                ],
                x + radius, yTop, // Start point
                [1, 1], // Scaling
                "F", // Fill only
                true // Close the path
            );

            // Set text color and add column names
            doc.setTextColor(255, 255, 255);
            doc.setFont("PlusJakartaSans-Bold", "normal");
            doc.text(i18n.t('CertificateList.column_course'), colStudy, y);
            doc.text(i18n.t('CertificateList.column_authorization'), colAuthorization, y);
            doc.text(i18n.t('CertificateList.column_date'), colDate, y);

            y += height;
        };

        // ** Add column headers before listing Learning Paths **
        startCoursesSection();
        addPageBackground();

        doc.setTextColor('#212120');
        doc.setFontSize(10);
        doc.text(`${i18n.t('CertificateList.page')} ${pageNumber}`, pageWidth - 20, 7);

        // ** Title: Study **
        doc.setFont("PPAgrandir-Medium", "normal");
        doc.setFontSize(25);
        doc.text(i18n.t('Certificates.certificate_title'), 10, y);
        y += 10;

        // ** User Name & Completion Text **
        doc.setFont("PlusJakartaSans-Medium", "normal");
        doc.setTextColor(71, 102, 255);
        doc.setFont('icomoon')
        doc.setTextColor(71, 102, 255);
        doc.setFontSize(17)
        //Crown Icon
        doc.text('', 10, y)

        doc.setFontSize(17);
        doc.setFont("PlusJakartaSans-Medium", "normal");
        doc.text(learningPathTitle , 19, y);
        y += lineHeight;
        doc.setFont("PlusJakartaSans-Regular", "normal");
        doc.setFontSize(12);
        doc.setTextColor('#212120');
        doc.text(i18n.t('CertificateList.includes_following_courses'), 10, y);
        y += lineHeight * 2;

        addColumnHeaders();

        doc.setTextColor('#212120');

        // Function to extract and format category-credits
        const getAuthorizations = (certificate) => {
            return certificate.durations?.map((d) => `${d.category}: ${d.credits}`);
        };

        // Loop through standalone courses
        coursesForDocument.forEach((course) => {

            // Handle instructors (support for array of instructor objects)
            if (Array.isArray(course.instructors)) {
                course.instructor = course.instructors.map(instructor => instructor.name).join(', ');
            }

            doc.setFont("PlusJakartaSans-Regular", "normal");
            const courseNumbering = `${courseNumber}.`;
            const wrappedTitle = doc.splitTextToSize(`${course.courseTitle}`, colAuthorization - colStudy - 30);
            const courseInfo = `${course.instructor || "Unknown"} | ${course.courseTypeValue || "N/A"} | ${convertMinutesToHours(course.duration) || "N/A"}`;
            const wrappedInfo = doc.splitTextToSize(courseInfo, colAuthorization - colStudy - 20);
            const authLines = getAuthorizations(course);
            const wrappedAuth = doc.splitTextToSize(authLines.join("\n"), colDate - colAuthorization - 10);

            // **Ensure full course fits on page before adding**
            ensureCourseFits(wrappedTitle, wrappedAuth, wrappedInfo);
            doc.setFontSize(10);
            // **Add course title**
            const spaceForNumber = +courseNumbering > 9 ? 8 : 6;
            doc.text(courseNumbering, colStudy + 2, y);
            doc.text(wrappedTitle, colStudy + spaceForNumber, y);

            // **Add authorizations (on the same line as the title)**
            doc.text(wrappedAuth, colAuthorization, y);
            doc.text(formattedDateForCountry(course.certificationDate) || "N/A", colDate, y);

            y += wrappedTitle.length * lineHeight - 2;
            doc.setFontSize(9);
            // **Add course extra info under title**
            doc.text(wrappedInfo, colStudy + 8, y);
            y += Math.max(wrappedInfo.length * infoLineHeight + 2, wrappedAuth.length * lineHeight);

            courseNumber++;
        });

        // Maybe refactor this duplicate code
        const totalDurationTitle = "Total Duration of Studies:";
        // --- Total Duration Section --- //
        // Calculate the total duration of studies
        let totalDuration = 0;
        coursesForDocument.forEach((course) => {
            totalDuration += course.duration || 0; // Summing up the durations of all courses
        });

        const textPaddingX = 6;
        const rowHeight = 7; // Height of each row (for background box)
        const rowSpacing = 3;

        // Calculate the total duration title width and total duration text width
        const totalDurationTitleWidth = doc.getTextWidth(totalDurationTitle) + textPaddingX * 2;
        const totalDurationText = `${convertMinutesToHours(totalDuration)}`;
        const totalDurationTextWidth = doc.getTextWidth(totalDurationText) + textPaddingX * 2;

        // Calculate remaining space on the page
        const pageHeightForLastContent = pageHeight - bottomMargin; // Space remaining from bottom margin, leaving some space for visual balance
        const requiredSpace = rowHeight + rowSpacing + totalDurationTextWidth; // Required space for the Total Duration section

        let finalYPosition = pageHeightForLastContent - (requiredSpace);

        // Place the Total Duration Title near the bottom of the page
        doc.setFont("PlusJakartaSans-Medium", "normal");
        doc.setFontSize(12);
        doc.setTextColor('#212120');
        doc.text(totalDurationTitle, colStudy + textPaddingX, finalYPosition + 10);
        // Draw the text for total duration
        doc.setFont("PlusJakartaSans-Regular", "normal");
        doc.setFontSize(10);
        doc.text(totalDurationText, colStudy + totalDurationTitleWidth + 3 + textPaddingX, finalYPosition + 10); // Position text inside the background

        let footerText = getFooterCompanyName()
        const footerTextY = pageHeight - (borderSize / 3); // Adjust to position in blue border

        doc.setFont("PlusJakartaSans-Regular", "normal");
        doc.setFontSize(12);
        doc.setTextColor(71, 102, 255);
        doc.text(footerText, pageWidth / 2, footerTextY, { align: "center" });

        return doc
}

function createListCertificate (data, userName) {
    let doc = getNewPdfObject();
    // Define page size, margins, and properties for landscape orientation
    const pageWidth = 297;  // A4 width in landscape mode
    const pageHeight = 210; // A4 height in landscape mode
    const bottomMargin = 5; // Space from bottom edge
    const lineHeight = 7;    // Space between lines
    const infoLineHeight = 5; // Smaller spacing for course info section
    const borderSize = 15;   // Border thickness for the content area
    const borderRadius = 5;  // Corner radius for the white content area

    // Define column positions
    const colStudy = 10;
    const colAuthorization = 140; // Moved left for more space
    const colDate = 240;
    const courseIndent = 15; // Increased indentation for Learning Path courses

    let y = 20;  // Initial Y position
    let pageNumber = 1;  // Start with Page 1
    let courseNumber = 1; // Numbering for standalone courses

    if(config.appLanguage === 'dk') {
        doc.addImage(listBackGroundImageDk.default.backgroundImage, 'PNG', 0, 0, 297, 210);
    } else {
        doc.addImage(listBackGroundImage.default.backgroundImage, 'PNG', 0, 0, 297, 210);
    }

    // Function to check if a new page is needed BEFORE adding a course
    const ensureCourseFits = (titleLines, authLines, infoLines, listIsLearningPath) => {
        const requiredSpace =
            titleLines.length * lineHeight +
            authLines.length * lineHeight +
            infoLines.length * infoLineHeight + 5;

        if (y + requiredSpace >= pageHeight - bottomMargin) {
            startCoursesSection(listIsLearningPath, true); // Move to a new page before adding course
        }
    };

    // Add page background color and rounded top border
    const addPageBackground = () => {
        if (pageNumber !== 1) {
            doc.setFillColor(227, 242, 255); // #E3F2FF light blue color
            doc.rect(0, 0, pageWidth, pageHeight, 'F'); // Fill entire page with the background color
        }
        doc.setFillColor(255, 255, 255); // White content area
        const startY = pageNumber === 1 ? y + 25 : y;
        doc.roundedRect(
            borderSize / 2,
            startY,
            pageWidth - borderSize,
            pageHeight - borderSize - startY,
            borderRadius,
            borderRadius,
            'F'
        );
    };

    // Function to check if a new page is needed
    const checkPageLimit = () => {
        if (y + lineHeight >= pageHeight - bottomMargin) {
            doc.addPage();
            pageNumber++;
            y = 20; // Reset Y position

            // Add page number at the top right corner
            doc.setTextColor('#212120');
            doc.setFontSize(10);
            doc.text(`${i18n.t('CertificateList.page')} ${pageNumber}`, pageWidth - 20, 7);

            // Re-add column headers on new page
            addColumnHeaders();
        }
    };

    // Ensure Courses section starts on a new page correctly, if we have learningpath content
    const startCoursesSection = (listIsLearningPath, secondCoursePage) => {
        if (data.learningPaths.length > 0 || secondCoursePage) {
            doc.addPage();
            pageNumber++;
            y = 20;
            addPageBackground();
            addColumnHeaders(); // First, add the headers so the title isn't above them.
        }

        doc.setTextColor('#212120');
        // Now, add the "Courses" section title
        doc.setFont("PPAgrandir-Medium", "normal");
        if (!listIsLearningPath && !secondCoursePage) {

            doc.setFont('icomoon')
            doc.setTextColor(71, 102, 255);
            doc.setFontSize(14)
            //Book icon
            doc.text('', 10, y + 2.5)

            doc.setFont("PlusJakartaSans-Bold", "normal");
            doc.setFontSize(12);
            doc.setTextColor('#212120');

            // Translations here *remove when added*
            doc.text(i18n.t('CertificateList.course_type_courses'), 17, y + 2)
        }
        doc.setFontSize(9);
        doc.setFont("PlusJakartaSans-Regular", "normal");
        if(pageNumber !== 1) {
            doc.setTextColor('#212120');
            doc.text(`${i18n.t('CertificateList.page')} ${pageNumber}`, pageWidth - 20, 7);
        }
        doc.setFontSize(10);
        y += lineHeight * 1.5;
    };

    addPageBackground();

    // Function to add column headers
    const addColumnHeaders = () => {
        doc.setFontSize(10);
        doc.setFillColor(71, 102, 255); // Blue color for header
        doc.setDrawColor(71, 102, 255); // Border color (optional)

        let x = colStudy - 2.7;
        let yTop = y - 7;
        let width = colDate - colStudy + 52.4;
        let height = lineHeight * 1.5;
        let radius = 2; // Radius for top corners

        // Begin custom drawing
        doc.setFillColor(71, 102, 255);
        doc.setDrawColor(71, 102, 255);

        // Create a path manually using lines and curves
        doc.lines(
            [
                [width - 2 * radius, 0], // Top horizontal line
                [radius, radius], // Top-right curve
                [0, height - radius], // Right vertical line
                [-width, 0], // Bottom horizontal line
                [0, -(height - radius)], // Left vertical line
                [radius, -radius], // Top-left curve
            ],
            x + radius, yTop, // Start point
            [1, 1], // Scaling
            "F", // Fill only
            true // Close the path
        );

        // Set text color and add column names
        doc.setTextColor(255, 255, 255);
        doc.setFont("PlusJakartaSans-Bold", "normal");
        doc.text(i18n.t('CertificateList.column_course'), colStudy, y);
        if(config.appLanguage !== 'dk') {
            doc.text(i18n.t('CertificateList.column_authorization'), colAuthorization, y);
        }
        doc.text(i18n.t('CertificateList.column_date'), colDate, y);

        y += height;
    };


    // Add first page number
    doc.setFontSize(9);
    doc.setFont("PlusJakartaSans-Regular", "normal");
    doc.setTextColor('#212120');
    doc.text(`${i18n.t('CertificateList.page')} ${pageNumber}`, pageWidth - 20, 7);

    const titleAndSubItemsHorizontal = 10;

    // ** Title: Study **
    doc.setFont("PPAgrandir-Medium", "normal");
    doc.setFontSize(27);
    doc.setTextColor('#011043');
    doc.text( i18n.t('CertificateList.certificatelist_title'), titleAndSubItemsHorizontal, y - 2);
    y += 10;

    // ** User Name & Completion Text **
    doc.setFont("PlusJakartaSans-Medium", "normal");
    doc.setTextColor(71, 102, 255);
    doc.setFontSize(18);
    doc.text(userName, titleAndSubItemsHorizontal, y);
    y += lineHeight;

    doc.setFont("PlusJakartaSans-Regular", "normal");
    doc.setFontSize(12);
    doc.setTextColor('#212120');
    // Translations here *remove when added*
    doc.text(i18n.t('CertificateList.has_completed_courses'), titleAndSubItemsHorizontal, y);
    y += lineHeight * 2;

    // ** Add column headers before listing Learning Paths **
    addColumnHeaders();

    if(data.learningPaths.length > 0) {
        doc.setTextColor('#212120');
        doc.setFont('icomoon')
        doc.setTextColor(71, 102, 255);
        doc.setFontSize(14)
        //Crown Icon
        doc.text('', 10, y + 2)

        // ** Learning Paths Section **
        doc.setFont("PlusJakartaSans-Bold", "normal");
        doc.setFontSize(12);
        doc.setTextColor('#212120');

        // Translations here *remove when added*
        doc.text(i18n.t('CertificateList.course_type_learning_paths'), 17, y + 2);
        y += lineHeight * 1.5;
    }
    doc.setFont("PlusJakartaSans-Regular", "normal");
    doc.setFontSize(10);

    // Function to extract and format category-credits
    const getAuthorizations = (certificate) => {
        return certificate.durations.map((d) => `${d.category}: ${d.credits}`);
    };

    // Loop through learning paths
    data.learningPaths?.forEach((learningPath, lpIndex) => {
        doc.setFontSize(11);
        doc.text(`${learningPath.title}`, colStudy, y);
        y += lineHeight;
        checkPageLimit();
        doc.setTextColor('#212120');
        learningPath.courses.forEach((course, courseIndex) => {
            doc.setFontSize(10);
            const courseNumbering = `${courseIndex + 1}.`;
            const wrappedTitle = doc.splitTextToSize(`${course.title}`, colAuthorization - colStudy - 30);
            const authLines = getAuthorizations(course.certificate);
            const wrappedAuth = doc.splitTextToSize(authLines.join("\n"), colDate - colAuthorization - 10);

            // Ensure all elements fit before placing
            ensureCourseFits(wrappedTitle, wrappedAuth, [], true);

            // ** Add course title **
            // use CourseIndent if needed
            const spaceForNumber = +courseNumbering > 9 ? 8 : 6;
            doc.text(courseNumbering, colStudy + 2, y);
            doc.text(wrappedTitle, colStudy + spaceForNumber, y);

            // ** Add authorizations on the same line as course title**
            if(config.appLanguage !== 'dk') {
                doc.text(wrappedAuth, colAuthorization, y);
            }
            // ** Add Date on the same line as course title**
            doc.text(formattedDateForCountry(course.certificate.certificationDate) || "N/A", colDate, y);

            y += wrappedTitle.length * lineHeight; // move to the next line after title
            // ** Add course extra info (instructor, entity, etc.) under the title**

            checkPageLimit();
        });

        y += lineHeight;
    });

    // ** Call this function right before starting Courses **
    startCoursesSection();

    let coursesForDocument = [];
    if(data.learningPaths.length > 0) {
        coursesForDocument = getLearningPathsExcludedCourseArray(data)
    } else {
        coursesForDocument = data.courses
    }

    // Loop through standalone courses
    coursesForDocument.forEach((course) => {
        const courseNumbering = `${courseNumber}.`;
        const wrappedTitle = doc.splitTextToSize(`${course.title}`, colAuthorization - colStudy - 30);
        const courseInfo = `${course.instructor || "Unknown"} | ${courseFormat(course.certificate) || "N/A"} | ${convertMinutesToHours(course.videoDuration) || "N/A"}`;
        const wrappedInfo = doc.splitTextToSize(courseInfo, colAuthorization - colStudy - 20);
        const authLines = getAuthorizations(course.certificate);
        const wrappedAuth = doc.splitTextToSize(authLines.join("\n"), colDate - colAuthorization - 10);

        // **Ensure full course fits on page before adding**
        ensureCourseFits(wrappedTitle, wrappedAuth, wrappedInfo);
        doc.setFontSize(10);
        // **Add course title**
        const spaceForNumber = +courseNumbering > 9 ? 8 : 6;
        doc.text(courseNumbering, colStudy + 2, y);
        doc.text(wrappedTitle, colStudy + spaceForNumber, y);

        // **Add authorizations (on the same line as the title)**
        if(config.appLanguage !== 'dk') {
            doc.text(wrappedAuth, colAuthorization, y);
        }
        doc.text(formattedDateForCountry(course.certificate.certificationDate) || "N/A", colDate, y);

        y += wrappedTitle.length * 4

        doc.setFontSize(9);
        // **Add course extra info under title**

        doc.text(wrappedInfo, colStudy + spaceForNumber, y);
        y += Math.max(wrappedInfo.length * infoLineHeight + 2, wrappedAuth.length * lineHeight);

        courseNumber++;
    });

    const authorizationSummary = {};
    data.courses.forEach((course) => {
        course.certificate.durations.forEach((duration) => {
            const key = `${duration.category}`;
            if (!authorizationSummary[key]) {
                authorizationSummary[key] = 0;
            }
            authorizationSummary[key] += duration.credits;
        });
    });

    // Define styles and padding for authorization and total duration sections

    // Translations here *remove when added*
    const totalDurationTitle = i18n.t('CertificateList.courses_watched_total')
    const authTitle = i18n.t('CertificateList.authorization_hours')
    const authBackgroundColor = [207, 230, 254]; // Light blue background color for each category
    const textPaddingX = 4;
    const textPaddingY = 3.8;
    const cornerRadius = 3.8;
    const maxRowWidth = pageWidth - colStudy - 10; // Maximum row width based on available space
    const rowHeight = 7; // Height of each row (for background box)
    const rowSpacing = 3;
    const itemSpacing = 2;

    let currentX = colStudy + 8; // Start X position for the text
    let currentY = y + 7; // Start Y position for the text
    let totalHeight = 0; // Total height for the authorizations section to manage spacing

    // --- Total Duration Section --- //
    // Calculate the total duration of studies
    let totalDuration = 0;
    data.courses.forEach((course) => {
        totalDuration += course.certificate.duration || 0; // Summing up the durations of all courses
    });

    // Calculate the total duration title width and total duration text width
    const totalDurationTitleWidth = doc.getTextWidth(totalDurationTitle) + textPaddingX * 2;
    const totalDurationText = `${convertMinutesToHours(totalDuration)}`;
    const totalDurationTextWidth = doc.getTextWidth(totalDurationText) + textPaddingX * 2;

    // Calculate remaining space on the page
    const pageHeightForLastContent = pageHeight - bottomMargin; // Space remaining from bottom margin, leaving some space for visual balance
    const requiredSpace = rowHeight + rowSpacing + totalDurationTextWidth; // Required space for the Total Duration section

    // Calculate Y position for the Total Duration Section to place it near the bottom
    let finalYPosition = pageHeightForLastContent - (requiredSpace);

    if (finalYPosition > 170) {  // In case there is not enough space, create a new page
        doc.addPage();
        y = 20;  // Initial Y position
        finalYPosition = 35; // Reset Y position after adding a new page
        addPageBackground(); // Add background for the new page
        addColumnHeaders();
        doc.setTextColor('#212120');
        pageNumber++;
        doc.setFontSize(9);
        doc.setFont("PlusJakartaSans-Regular", "normal");
        if(pageNumber !== 1) {
            doc.setTextColor('#212120');
            doc.text(`${i18n.t('CertificateList.page')} ${pageNumber}`, pageWidth - 20, 7);
        }
    }

    // Place the Total Duration Title near the bottom of the page
    doc.setFont("PlusJakartaSans-Medium", "normal");
    doc.setFontSize(11);
    doc.setTextColor('#212120');

    if(config.appLanguage === 'dk') {
        // This is the position where the authorizations normally are
        finalYPosition = 179
    }

    doc.text(totalDurationTitle, colStudy + textPaddingX, finalYPosition);

    // Draw the text for total duration
    doc.setFont("PlusJakartaSans-Regular", "normal");
    doc.setFontSize(11);
    doc.text(totalDurationText, colStudy + totalDurationTitleWidth + 4 + textPaddingX, finalYPosition); // Position text inside the background

    finalYPosition += rowHeight + rowSpacing; // Move Y position after the total duration section

    // --- Authorization Section --- //
    // Authorization Title
    doc.setFont("PlusJakartaSans-Medium", "normal");
    doc.setFontSize(11);
    doc.setTextColor('#212120');

    // Set the authorization tags below, skip this for DK only
    function renderAuthorizationSummary({
        doc,
        authorizationSummary,
        authTitle,
        colStudy,
        finalYPosition,
        textPaddingX,
        textPaddingY,
        rowHeight,
        rowSpacing,
        itemSpacing,
        pageWidth,
        authBackgroundColor,
        cornerRadius,
        currentX
    }) {
        const authTitleWidth = doc.getTextWidth(authTitle) + textPaddingX * 2;
        currentX += 3; // Reset X position for the authorization section

        finalYPosition -= 4; // Adjust Y position for the authorization section


        if (Object.keys(authorizationSummary).length > 5 && finalYPosition < 150) {  // In case there is not enough space, create a new page
            doc.addPage();
            y = 20;  // Initial Y position
            finalYPosition = 35; // Reset Y position after adding a new page
            addPageBackground(); // Add background for the new page
            addColumnHeaders();
            doc.setTextColor('#212120');
            pageNumber++;
            doc.setFontSize(9);
            doc.setFont("PlusJakartaSans-Regular", "normal");
            if(pageNumber !== 1) {
                doc.setTextColor('#212120');
                doc.text(`${i18n.t('CertificateList.page')} ${pageNumber}`, pageWidth - 20, 7);
            }
            doc.setFontSize(11);
            doc.setFont("PlusJakartaSans-Medium", "normal");
        }

        if (authorizationSummary) {
            doc.text(authTitle, colStudy + textPaddingX, finalYPosition + 1);
        }

        Object.keys(authorizationSummary).forEach((category) => {
            const credits = authorizationSummary[category];

            // Set fonts and measure text width for both parts
            doc.setFont("PlusJakartaSans-Bold", "normal");
            doc.setFontSize(9);
            const categoryWidth = doc.getTextWidth(`${category}: `);

            doc.setFont("PlusJakartaSans-Regular", "normal");
            const creditsWidth = doc.getTextWidth(`${credits.toFixed(2)} credits`);

            const textWidth = categoryWidth + creditsWidth + textPaddingX * 2;

            if (currentX + textWidth + itemSpacing > pageWidth - 10) {
                currentX = colStudy + 8;
                finalYPosition += rowHeight + rowSpacing;
            }

            doc.setDrawColor('#ADBBFF');
            doc.setLineWidth(0.3);
            doc.setFillColor(...authBackgroundColor);
            doc.roundedRect(
                currentX + authTitleWidth - 12, finalYPosition - textPaddingY,
                textWidth, rowHeight,
                cornerRadius, cornerRadius, "F"
            );

            // Draw category in bold
            doc.setFont("PlusJakartaSans-Bold", "normal");
            doc.setFontSize(9);
            doc.text(`${category}: `, currentX + textPaddingX + authTitleWidth - 12, finalYPosition + 1);

            // Draw credits in regular, shifted right by categoryWidth
            doc.setFont("PlusJakartaSans-Regular", "normal");
            doc.text(`${credits.toFixed(2)} credits`, currentX + textPaddingX + authTitleWidth - 12 + categoryWidth, finalYPosition + 1);

            currentX += textWidth + itemSpacing;
        });

        return { finalYPosition, currentX }; // Return any updated positions if needed
    }

    if (config.appLanguage !== 'dk') {
        ({ finalYPosition, currentX } = renderAuthorizationSummary({
            doc,
            authorizationSummary,
            authTitle,
            colStudy,
            finalYPosition,
            textPaddingX,
            textPaddingY,
            rowHeight,
            rowSpacing,
            itemSpacing,
            pageWidth,
            authBackgroundColor,
            cornerRadius,
            currentX
        }));
    }

    finalYPosition += rowHeight + rowSpacing; // Update Y position after authorization section

    let footerText = getFooterCompanyName()
    const footerTextY = pageHeight - (borderSize / 3); // Adjust to position in blue border

    doc.setFont("PlusJakartaSans-Regular", "normal");
    doc.setFontSize(12);
    doc.setTextColor(71, 102, 255);
    doc.text(footerText, pageWidth / 2, footerTextY, { align: "center" });

    // Set document properties
    doc.setProperties({
        title: i18n.t('Certificates.training_certificate_list')
    });

    // Open the document
    window.open(doc.output('bloburl'));
}

function getLearningPathsExcludedCourseArray (data) {
    let learningPathCourseIds = new Set();  // Using a Set for fast lookup and uniqueness

    data.learningPaths.forEach((learningpath) => {
        learningpath.courses.forEach((course) => {
            learningPathCourseIds.add(course.id);  // Add course id from learningpath to Set
        });
    });

    // Step 2: Filter the courses that are NOT in the learning paths
    const coursesForDocument = data.courses.filter((course) => {
        // Return courses that are not in the learning path courses
        return !learningPathCourseIds.has(course.id);
    });

    return coursesForDocument;
}


function courseFormat (course) {
    const typeToTranslation = {
        micro: 'coursetype_microlearning',
        course: 'coursetype_course',
        webinar: 'coursetype_webinar',
        recording: 'coursetype_recording',
        article: 'coursetype_article',
        wpcourse: 'coursetype_wpcourse',
        compilation: 'coursetype_compilation',
        virtual_classroom: 'coursetype_virtual_classroom',
        podcast: 'coursetype_podcast',
        test_only: 'test-only'
    }

    if (course.entityType === 'learningPath' && Array.isArray(course.courseTypeValue)) {
        return course.courseTypeValue
            .map(type => i18n.t(typeToTranslation[type.value]) || type.value)
            .join(', ');
    }

    return i18n.t(typeToTranslation[course.courseTypeValue]) || course.courseTypeValue
}

function getFooterCompanyName () {
    let footerText = ''
    if (config.appLanguage === 'dk') {
        footerText = `edu.golearn.${config.appLanguage}`
    } else {
        footerText = `eduhouse.${config.appLanguage}`
    }
    return footerText
}

function getNewPdfObject () {
    let doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4"
    });


    doc.addFileToVFS('icomoon-normal.ttf', fontIcomoonNormal)
    doc.addFont('icomoon-normal.ttf', 'icomoon', 'normal')
    doc.addFileToVFS('PPAgrandir-Medium.ttf', PPAgrandirMedium)
    doc.addFont('PPAgrandir-Medium.ttf', 'PPAgrandir-Medium', 'normal')
    doc.addFileToVFS('PlusJakartaSans-Regular.ttf', PlusJakartaSansRegular)
    doc.addFont('PlusJakartaSans-Regular.ttf', 'PlusJakartaSans-Regular', 'normal')
    doc.addFileToVFS('PlusJakartaSans-Medium.ttf', PlusJakartaSansMedium)
    doc.addFont('PlusJakartaSans-Medium.ttf', 'PlusJakartaSans-Medium', 'normal')
    doc.addFileToVFS('PlusJakartaSans-Bold.ttf', PlusJakartaSansBold)
    doc.addFont('PlusJakartaSans-Bold.ttf', 'PlusJakartaSans-Bold', 'normal')
    return doc
}

export default {
    createSingleCertificate,
    createListCertificate,
}
